import type { MouseEvent, SyntheticEvent } from "react";
import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useSearchParams } from "react-router";

import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BuildingIcon,
  CheckIcon,
  CircleAlertIcon,
  ContactIcon,
  LockIcon,
  PencilIcon,
  SettingsIcon,
  UsersIcon,
} from "lucide-react";

import { asMutation, initUseQuery } from "simplydo/core/query";
import { getUserDomain, isCommonDomain } from "simplydo/core/users";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { Icon } from "simplydo/react/ui/icon";
import { MenuItem } from "simplydo/react/ui/menu";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalDimmer,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Tab } from "simplydo/react/ui/tab";

import api from "api";
import Prompt from "utils/Prompt";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import Contact from "./Tabs/Contact";
import { EditOverview } from "./Tabs/EditOverview";
import { Overview } from "./Tabs/Overview";
import { Roles } from "./Tabs/Roles";
import { Settings } from "./Tabs/Settings";
import { Users } from "./Tabs/Users";
import type {
  IdeaBusinessProfile,
  IdeaBusinessProfileUpdate,
  IdeaBusinessProfileUpdateResponse,
} from "./types";

type IIdeaBusinessProfileProps = {
  businessProfile: IdeaBusinessProfile;
  onClose: () => void;
  onSave?: (ideaBusinessProfile: IdeaBusinessProfile) => void;
  onDelete?: (ideaBusinessProfile: IdeaBusinessProfile) => void;
  config?: { openInEditMode?: boolean; defaultTab?: number };
};

const IdeaBusinessProfileModal = ({
  businessProfile: initialProfile,
  onClose,
  onSave,
  onDelete,
  config,
}: IIdeaBusinessProfileProps) => {
  const theme = useTheme();

  const profileQuery = useQuery(
    initUseQuery(api.users.getProfile, [initialProfile?._id], {
      isEnabled: !!initialProfile?._id,
      initialData: { ideaBusinessProfile: initialProfile },
      initialDataUpdatedAt: 0, // Force initial stale data to be refetched
    }),
  );
  const profileId = profileQuery.data?.ideaBusinessProfile?._id;

  const user = useCurrentUser();
  const userDomain = user ? getUserDomain(user) : undefined;

  const [searchParams, setSearchParams] = useSearchParams();
  const tabActiveIndex = parseInt(
    searchParams.get("businessProfileTab") ||
      (config?.defaultTab || 0).toString(),
    10,
  );

  const canEditProfile = util.hasPermission(
    user,
    "ideaBusinessProfile.editDetails",
    profileId,
  );

  const [isUnsaved, setUnsaved] = useState(false);
  const [isEditing, setIsEditing] = useState(
    canEditProfile && config?.openInEditMode,
  );

  const onProfileUpdated = (businessProfile: IdeaBusinessProfile) => {
    api.setQueryData(api.users.getProfiles, [], (prev) => ({
      ...prev,
      profiles: prev.profiles.map((profile) =>
        profile._id === businessProfile._id ? businessProfile : profile,
      ),
    }));
    api.invalidateQueries(api.users.getProfiles);

    setUnsaved(false);
    onSave?.(businessProfile);
  };

  const updateProfileMutation = useMutation({
    mutationKey: [
      "post",
      "users",
      "profiles",
      profileQuery.data?.ideaBusinessProfile?._id,
    ],
    mutationFn: asMutation(api.users.updateProfile),
    onSuccess: (data: IdeaBusinessProfileUpdateResponse) => {
      toast.success("Profile updated");
      onProfileUpdated(data.ideaBusinessProfile);
    },
    onError: () => {
      toast.error("Failed to update profile");
    },
  });

  const updateProfile = useCallback(
    (profile: IdeaBusinessProfileUpdate) => {
      if (!profileId) {
        return;
      }
      updateProfileMutation.mutate([profileId, profile]);
    },
    [profileId, updateProfileMutation],
  );

  const updateActiveTab = useCallback(
    (newIndex: number | string) => {
      setSearchParams((prev) => {
        prev.set("businessProfileTab", newIndex.toString());
        return prev;
      });
    },
    [setSearchParams],
  );

  const onCloseModal = useCallback(() => {
    if (isUnsaved) {
      util
        .confirm(
          "Unsaved changes",
          "Unsaved changes will be lost if the modal is closed. Are you sure you want to proceed and discard your changes?",
        )
        .then(() => {
          onClose?.();
          setSearchParams((prev) => {
            prev.delete("businessProfileTab");
            return prev;
          });
        })
        .catch(() => {});
    } else {
      onClose?.();
      setSearchParams((prev) => {
        prev.delete("businessProfileTab");
        return prev;
      });
    }
  }, [onClose, setSearchParams, isUnsaved]);

  const ideaBusinessProfile = profileQuery.data?.ideaBusinessProfile;

  const tabPanes = useMemo(() => {
    if (!ideaBusinessProfile) {
      return [];
    }

    return [
      {
        menuItem: (
          <MenuItem tabIndex={0} key="profile">
            <Icon icon={BuildingIcon} />
            Profile
          </MenuItem>
        ),
        render: () =>
          isEditing ? (
            <EditOverview
              businessProfile={ideaBusinessProfile}
              setUnsaved={setUnsaved}
            />
          ) : (
            <Overview businessProfile={ideaBusinessProfile} />
          ),
      },
      util.hasPermission(
        user,
        "ideaBusinessProfile.viewDetails",
        ideaBusinessProfile._id,
      ) || (ideaBusinessProfile.contacts?.length ?? 0) > 0
        ? {
            menuItem: (
              <MenuItem tabIndex={0} key="addresses">
                <Icon icon={ContactIcon} />
                Addresses&nbsp;&&nbsp;Contacts
                {!ideaBusinessProfile.contacts?.length ? (
                  <Icon
                    icon={CircleAlertIcon}
                    color="red"
                    size="small"
                    style={{ marginTop: -8, marginLeft: 2, width: 0 }}
                  />
                ) : null}
              </MenuItem>
            ),
            render: () => (
              <Contact businessProfile={ideaBusinessProfile} onSave={onSave} />
            ),
          }
        : null,
      util.hasPermission(
        user,
        "ideaBusinessProfile.viewMembers",
        ideaBusinessProfile._id,
      )
        ? {
            menuItem: (
              <MenuItem tabIndex={0} key="users">
                <Icon icon={UsersIcon} />
                Users
              </MenuItem>
            ),
            render: () => (
              <Users
                businessProfile={ideaBusinessProfile}
                onCloseModal={onCloseModal}
              />
            ),
          }
        : null,
      util.hasPermission(
        user,
        "ideaBusinessProfile.deleteProfile",
        ideaBusinessProfile._id,
      )
        ? {
            menuItem: (
              <MenuItem tabIndex={0} key="settings">
                <Icon icon={SettingsIcon} />
                Settings
              </MenuItem>
            ),
            render: () => (
              <Settings
                businessProfile={ideaBusinessProfile}
                updateProfile={updateProfile}
                onCloseModal={onCloseModal}
                onDelete={onDelete}
              />
            ),
          }
        : null,
      util.hasPermission(
        user,
        "ideaBusinessProfile.editMembers",
        ideaBusinessProfile._id,
      )
        ? {
            menuItem: (
              <MenuItem key="roles" tabIndex={0} position="right">
                <Icon icon={LockIcon} />
                Roles
              </MenuItem>
            ),
            render: () => <Roles businessProfile={ideaBusinessProfile} />,
          }
        : null,
    ].filter((pane): pane is NonNullable<typeof pane> => pane !== null);
  }, [
    ideaBusinessProfile,
    isEditing,
    onCloseModal,
    onDelete,
    onSave,
    updateProfile,
    user,
  ]);

  if (!profileQuery.isSuccess || !ideaBusinessProfile) {
    return null;
  }

  return (
    <Modal
      open={!!ideaBusinessProfile}
      dimmer={
        <ModalDimmer
          onClick={(e: MouseEvent<HTMLDivElement>) => {
            if (e.target !== e.currentTarget) {
              return;
            }
            onCloseModal();
          }}
        />
      }
      closeOnDimmerClick={!isUnsaved}
      closeOnEscape={!isUnsaved}
    >
      <Prompt
        when={isUnsaved}
        message="Unsaved changes will be lost if the modal is closed. Are you sure you want to proceed and discard your changes?"
        shouldBlock={(event) =>
          event.nextLocation.pathname !== event.currentLocation.pathname
        }
      />
      <ModalHeader>{ideaBusinessProfile.name}</ModalHeader>
      <ModalContent scrolling style={{ height: "fit-content" }}>
        {util.hasPermission(
          user,
          "ideaBusinessProfile.viewMembers",
          ideaBusinessProfile._id,
        ) ||
        util.hasPermission(
          user,
          "ideaBusinessProfile.editMembers",
          ideaBusinessProfile._id,
        ) ? (
          <Tab
            variant="regular"
            menu={{
              style: {
                overflowY: "hidden",
                overflowX: "auto",
                scrollbarWidth: "thin",
                paddingBottom: 6,
              },
            }}
            activeIndex={tabActiveIndex}
            onTabChange={(_e: SyntheticEvent, { activeIndex }) => {
              if (activeIndex !== undefined) {
                updateActiveTab(activeIndex);
              }
            }}
            style={{ position: "relative" }}
            panes={tabPanes}
          />
        ) : isEditing ? (
          <EditOverview
            businessProfile={ideaBusinessProfile}
            setUnsaved={setUnsaved}
          />
        ) : (
          <Overview businessProfile={ideaBusinessProfile} />
        )}
      </ModalContent>
      <ModalActions>
        {canEditProfile ? (
          isEditing ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                paddingBottom: 12,
              }}
            >
              {theme.sizes.isComputer &&
              userDomain &&
              !isCommonDomain(userDomain) ? (
                <div style={{ display: "flex", textAlign: "left" }}>
                  <Checkbox
                    toggle
                    style={{ marginBottom: 12 }}
                    checked={
                      !!ideaBusinessProfile.emailDomains?.find(
                        (domain) => domain.domain === userDomain,
                      )
                    }
                    onChange={(_e, d) => {
                      const domains = ideaBusinessProfile.emailDomains || [];

                      api.setQueryData(
                        api.users.getProfile,
                        [ideaBusinessProfile._id],
                        (prev) => ({
                          ideaBusinessProfile: {
                            ...prev.ideaBusinessProfile,
                            emailDomains: d.checked
                              ? [...domains, { domain: userDomain }]
                              : domains.filter(
                                  (domain) => domain.domain !== userDomain,
                                ),
                          },
                        }),
                      );
                    }}
                  >
                    <CheckboxHeader>
                      {`Open for other @${userDomain} users`}
                    </CheckboxHeader>
                    <CheckboxContent>
                      Users with the same domain can join this profile.
                    </CheckboxContent>
                  </Checkbox>
                </div>
              ) : null}
              <div>
                <Button
                  style={{ marginRight: 10 }}
                  onClick={() => {
                    if (isUnsaved) {
                      util
                        .confirm(
                          "Unsaved changes",
                          "Unsaved changes will be lost. Are you sure you want to proceed and discard your changes?",
                        )
                        .then(() => {
                          setUnsaved(false);
                          setIsEditing(false);
                          api.setQueryData(
                            api.users.getProfile,
                            [ideaBusinessProfile._id],
                            profileQuery.data,
                          );
                        })
                        .catch(() => {});
                    } else {
                      setUnsaved(false);
                      setIsEditing(false);
                      api.setQueryData(
                        api.users.getProfile,
                        [profileQuery.data.ideaBusinessProfile?._id],
                        profileQuery.data,
                      );
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  loading={updateProfileMutation.isPending}
                  onClick={() => {
                    updateProfileMutation.mutate(
                      [ideaBusinessProfile._id, ideaBusinessProfile],
                      {
                        onSuccess: () => {
                          setIsEditing(false);
                        },
                      },
                    );
                  }}
                  color="primary"
                >
                  <Icon icon={CheckIcon} />
                  Save changes
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => {
                updateActiveTab(0);
                setIsEditing(true);
              }}
              variant="outline"
            >
              <Icon icon={PencilIcon} />
              Edit profile
            </Button>
          )
        ) : null}
        {isEditing ? null : <Button onClick={onCloseModal}>Close</Button>}
      </ModalActions>
    </Modal>
  );
};

export default IdeaBusinessProfileModal;
